import React from 'react';
import ModernCTA from '../../components/ModernCTA';
import { motion } from 'framer-motion';
import '../../styles/Services.css';
import { Link } from 'react-router-dom';

// Premium Icons
import { FiCode, FiRefreshCw, FiServer, FiShoppingCart, FiTrendingUp } from 'react-icons/fi';
import { AiOutlineRobot, AiOutlineCloudServer } from 'react-icons/ai';
import { MdHealthAndSafety, MdPrecisionManufacturing, MdAgriculture } from 'react-icons/md';
import { BsPhone } from 'react-icons/bs';

const ServicesPage = () => {
  const services = [
    {
      id: 1,
      title: "Web Development",
      icon: <FiCode className="service-icon" />,
      brief: "Build lightning-fast, SEO-optimized websites with React, Next.js, and cutting-edge Jamstack architecture.",
      link: "/services/web-development"
    },
    {
      id: 2,
      title: "Web Re-Development",
      icon: <FiRefreshCw className="service-icon" />,
      brief: "Modernize legacy systems with microservices architecture and cloud-native solutions.",
      link: "/services/web-redevelopment"
    },
    {
      id: 3,
      title: "Web Maintenance",
      icon: <FiServer className="service-icon" />,
      brief: "24/7 monitoring with automated alerts, zero-downtime deployments, and performance audits.",
      link: "/services/web-maintenance"
    },
    {
      id: 4,
      title: "E-Commerce Solutions",
      icon: <FiShoppingCart className="service-icon" />,
      brief: "Headless commerce platforms with Shopify Plus, BigCommerce, and custom PWA implementations.",
      link: "/services/ecommerce-services"
    },
    {
      id: 5,
      title: "Digital Growth (SEO)",
      icon: <FiTrendingUp className="service-icon" />,
      brief: "Enterprise SEO with technical audits, content clusters, and AI-powered keyword strategy.",
      link: "/services/web-promotion-seo"
    },
    {
      id: 6,
      title: "Enterprise Software",
      icon: <MdPrecisionManufacturing className="service-icon" />,
      brief: "Custom ERP/CRM solutions with machine learning integrations and predictive analytics.",
      link: "/services/software-development"
    },
    {
      id: 7,
      title: "Cloud Infrastructure",
      icon: <AiOutlineCloudServer className="service-icon" />,
      brief: "AWS/GCP architecture with Kubernetes orchestration and infrastructure-as-code.",
      link: "/services/hardware-networking"
    },
    {
      id: 8,
      title: "Mobile Innovation",
      icon: <BsPhone className="service-icon" />,
      brief: "Cross-platform apps with Flutter and React Native featuring AR/VR capabilities.",
      link: "/services/mobile-application"
    },
    {
      id: 9,
      title: "AI Surveillance",
      icon: <AiOutlineRobot className="service-icon" />,
      brief: "Smart city solutions with facial recognition, anomaly detection, and predictive policing.",
      link: "/services/ai-surveillance"
    },
    {
      id: 10,
      title: "AgriTech AI",
      icon: <MdAgriculture className="service-icon" />,
      brief: "Precision farming with drone imagery analysis and automated irrigation systems.",
      link: "/services/ai-agriculture"
    },
    {
      id: 11,
      title: "HealthTech AI",
      icon: <MdHealthAndSafety className="service-icon" />,
      brief: "Diagnostic imaging analysis, drug discovery platforms, and patient monitoring systems.",
      link: "/services/ai-healthcare"
    },
    {
      id: 12,
      title: "IT Consulting",
      icon: <FiServer className="service-icon" />,
      brief: "Strategic technology roadmaps, digital transformation advisory, and IT infrastructure optimization.",
      link: "/services/it-consulting"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 40, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 15,
        stiffness: 100
      }
    },
    hover: {
      y: -10,
      transition: { 
        type: "spring",
        stiffness: 300
      }
    }
  };

  return (
    <div className="services-container">
      {/* Hero Section */}
      <motion.section 
        className="services-hero"
        initial={{ opacity: 0, y: -40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ 
          type: "spring",
          damping: 10,
          stiffness: 100
        }}
      >
        <div className="hero-content">
          <h1 className="hero-title">
            Transformative <span>Digital Solutions</span> for the Modern Enterprise
          </h1>
          <p className="hero-subtitle">
            PCDS Infotech delivers cutting-edge technology services that drive measurable business outcomes
          </p>
        </div>
        <div className="hero-stats">
          <div className="stat-item">
            <div className="stat-number">200+</div>
            <div className="stat-label">Successful Projects</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">15+</div>
            <div className="stat-label">Years Experience</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">98%</div>
            <div className="stat-label">Client Retention</div>
          </div>
        </div>
      </motion.section>

      {/* Services Introduction */}
      <section className="services-intro-section">
        <motion.div 
          className="intro-content"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="section-header">
            <h2 className="section-title">Our <span>Service</span> Philosophy</h2>
          </div>
          <div className="intro-grid">
            <div className="intro-card">
              <div className="intro-icon">✓</div>
              <h3>Client-Centric Approach</h3>
              <p>Every solution begins with understanding your unique business challenges and goals.</p>
            </div>
            <div className="intro-card">
              <div className="intro-icon">⚡</div>
              <h3>Technology Agnostic</h3>
              <p>We select the right stack for each project, not just what we're comfortable with.</p>
            </div>
            <div className="intro-card">
              <div className="intro-icon">🔄</div>
              <h3>Iterative Development</h3>
              <p>Continuous delivery with agile sprints ensures we're always moving forward.</p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Main Services Grid */}
      <section className="services-main">
        <motion.div 
          className="services-grid-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          <div className="section-header">
            <h2 className="section-title">Our <span>Core</span> Offerings</h2>
            <p className="section-description">
              Comprehensive digital transformation services tailored to your industry needs
            </p>
          </div>
        </motion.div>

        <motion.div 
          className="services-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {services.map((service) => (
            <motion.div 
              key={service.id}
              className="service-card"
              variants={itemVariants}
              whileHover="hover"
              whileTap={{ scale: 0.98 }}
            >
              <div className="card-inner">
                <div className="service-icon-container">
                  {service.icon}
                </div>
                <h3>{service.title}</h3>
                <p>{service.brief}</p>
                <Link to={service.link} className="learn-more-btn">
                  <span>Explore Service</span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </Link>
              </div>
              <div className="card-hover-effect"></div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Technology Stack */}
      <section className="tech-stack-section">
        <motion.div 
          className="tech-stack-content"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          <div className="section-header">
            <h2 className="section-title">Our <span>Technology</span> Stack</h2>
          </div>
          <div className="tech-stack-grid">
            <div className="tech-stack-item">
              <div className="tech-logo react"></div>
              <span>React/Next.js</span>
            </div>
            <div className="tech-stack-item">
              <div className="tech-logo node"></div>
              <span>Node.js</span>
            </div>
            <div className="tech-stack-item">
              <div className="tech-logo python"></div>
              <span>Python</span>
            </div>
            <div className="tech-stack-item">
              <div className="tech-logo tensorflow"></div>
              <span>TensorFlow</span>
            </div>
            <div className="tech-stack-item">
              <div className="tech-logo aws"></div>
              <span>AWS</span>
            </div>
            <div className="tech-stack-item">
              <div className="tech-logo gcp"></div>
              <span>Google Cloud</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* CTA Section */}
      <section className="services-cta-section">
        <ModernCTA />
      </section>
    </div>
  );
};

export default ServicesPage;